// src/lib/posthog/config.ts
//
// The two PostHog env vars, read literally so Next.js can inline them at
// build time, plus the EU host fallback.
//
// `getPosthogClientEnv` reads only the two NEXT_PUBLIC_* vars. The public
// project key is the only PostHog credential this app holds.

const EU_INGEST_HOST = "https://eu.i.posthog.com";
const EU_API_HOST = "https://eu.posthog.com";

function isAbsoluteHttpsUrl(value: string | undefined): value is string {
  if (!value) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/** NEXT_PUBLIC_POSTHOG_HOST falls back to the EU ingestion host. */
export function resolveIngestHost(rawHost: string | undefined): string {
  if (isAbsoluteHttpsUrl(rawHost)) return rawHost;
  console.error(
    `[posthog] NEXT_PUBLIC_POSTHOG_HOST is not an absolute https URL (got ${JSON.stringify(rawHost ?? "")}); falling back to ${EU_INGEST_HOST}`,
  );
  return EU_INGEST_HOST;
}

export interface PosthogClientEnv {
  /** NEXT_PUBLIC_POSTHOG_KEY, empty when unset (capture no-ops on an empty key). */
  key: string;
  /** Resolved NEXT_PUBLIC_POSTHOG_HOST, used as posthog-js's `api_host`. */
  ingestHost: string;
  /** Fixed EU UI/API host, used as posthog-js's `ui_host`. Never read from a
   *  server-only env var, since this value ships in the client bundle. */
  uiHost: string;
}

/** Client-safe env: reads only the two NEXT_PUBLIC_* vars, literally. */
export function getPosthogClientEnv(): PosthogClientEnv {
  return {
    key: process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "",
    ingestHost: resolveIngestHost(process.env.NEXT_PUBLIC_POSTHOG_HOST),
    uiHost: EU_API_HOST,
  };
}
